import React, { useState, useEffect } from 'react';

const OpponentDetailsOverlay = ({ opponent, onClose, onSave, onDelete }) => {
  const [formData, setFormData] = useState({
    id: null,
    firstName: '',
    lastName: '',
    utr: '',
    club: '',
    wtn: '',
  });

  useEffect(() => {
    if (opponent) {
      setFormData({
        id: opponent.id,
        firstName: opponent.firstName || '',
        lastName: opponent.lastName || '',
        utr: opponent.utr || '',
        club: opponent.club || '',
        wtn: opponent.wtn || '',
      });
    } else {
      setFormData({
        id: null,
        firstName: '',
        lastName: '',
        utr: '',
        club: '',
        wtn: '',
      });
    }
  }, [opponent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const matchCount = opponent ? (parseInt(opponent.matches_count || opponent.total_matches, 10) || 0) : 0;
  const isEditing = Boolean(opponent && opponent.id);
  const canDelete = isEditing && matchCount === 0;

  const handleDelete = () => {
    if (!canDelete || !onDelete) return;
    if (window.confirm(`Are you sure you want to delete ${formData.firstName} ${formData.lastName}? This cannot be undone.`)) {
      onDelete(opponent.id);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Opponent' : 'Add New Opponent'}
          </h2>
          {isEditing && (
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
              matchCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
            }`}>
              {matchCount > 0 ? `🎮 ${matchCount} ${matchCount === 1 ? 'Match' : 'Matches'}` : '✨ Never in a match'}
            </span>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="firstName" className="block text-gray-700 text-sm font-bold mb-1">
              First Name:
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="mb-4">
            <label htmlFor="lastName" className="block text-gray-700 text-sm font-bold mb-1">
              Last Name:
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label htmlFor="utr" className="block text-gray-700 text-sm font-bold mb-1">
                UTR (Optional):
              </label>
              <input
                type="text"
                id="utr"
                name="utr"
                value={formData.utr}
                onChange={handleChange}
                className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="wtn" className="block text-gray-700 text-sm font-bold mb-1">
                WTN (Optional):
              </label>
              <input
                type="text"
                id="wtn"
                name="wtn"
                value={formData.wtn}
                onChange={handleChange}
                className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="mb-4">
            <label htmlFor="club" className="block text-gray-700 text-sm font-bold mb-1">
              Club (Optional):
            </label>
            <input
              type="text"
              id="club"
              name="club"
              value={formData.club}
              onChange={handleChange}
              className="shadow-sm appearance-none border border-gray-300 rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {isEditing && matchCount > 0 && (
            <div className="p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              ℹ️ This opponent is associated with {matchCount} match records and cannot be deleted. You can only edit their details.
            </div>
          )}

          <div className="flex justify-between items-center mt-6">
            <div>
              {canDelete && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 text-sm font-medium transition-colors"
                >
                  Delete Opponent
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors shadow-sm"
              >
                {isEditing ? 'Save Changes' : 'Add Opponent'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OpponentDetailsOverlay;
